import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '@/components/Layout';
import { API } from '@/App';
import { 
  Building2, Upload, Trash2, Save, Image as ImageIcon, 
  Loader2, Phone, Mail, MapPin, FileText, Check
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

export default function VendorSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const fileInputRef = useRef(null);
  
  const [profile, setProfile] = useState({
    display_name: '',
    company_name: '',
    contact_person: '',
    phone: '',
    email: '',
    address: '',
    gstin: '',
    pan: '',
    description: '',
    logo_url: null,
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/vendor/settings`);
      // iter-386u-k — /vendor/settings returns { profile, branding }.
      // Read from the nested shape so saved data displays on load.
      const p = response.data?.profile || {};
      const b = response.data?.branding || {};
      setProfile({
        display_name: p.name || '',
        company_name: p.company_name || '',
        contact_person: p.contact_person || p.name || '',
        phone: p.phone || '',
        email: p.email || '',
        address: p.address || '',
        gstin: p.gstin || '',
        pan: p.pan || '',
        description: p.description || '',
        // Prefer the durable data URL (renders directly from DB base64);
        // fall back to file path if we ever go back to filesystem serving.
        logo_url: b.logo_data_url || b.logo_url || null,
      });
    } catch (error) {
      console.error('Failed to load settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // iter-386u-k — persist Display Name (User.name) alongside company fields.
      const payload = { ...profile, name: profile.display_name };
      delete payload.display_name;
      delete payload.logo_url;
      await axios.put(`${API}/vendor/settings/profile`, payload);
      // Keep localStorage user.name in sync so the sidebar / avatar update instantly.
      try {
        const stored = JSON.parse(localStorage.getItem('user') || '{}');
        localStorage.setItem('user', JSON.stringify({ ...stored, name: profile.display_name }));
      } catch { /* noop */ }
      toast.success('Profile updated successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Use PNG, JPG, or WEBP');
      return;
    }

    // Validate file size (2MB max)
    if (file.size > 2 * 1024 * 1024) {
      toast.error('File too large. Max 2MB allowed');
      return;
    }

    setUploadingLogo(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/vendor/settings/logo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      // iter-386u-k — use the durable data URL for instant preview.
      setProfile({ ...profile, logo_url: response.data.logo_data_url || response.data.logo_url });
      toast.success('Logo uploaded successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleRemoveLogo = async () => {
    try {
      await axios.delete(`${API}/vendor/settings/logo`);
      setProfile({ ...profile, logo_url: null });
      toast.success('Logo removed');
    } catch (error) {
      toast.error('Failed to remove logo');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="animate-spin text-primary" size={32} />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 max-w-3xl mx-auto" data-testid="vendor-settings-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-stone-900">Company Settings</h1>
            <p className="text-stone-500">Manage your company profile and branding</p>
          </div>
          <Button onClick={handleSave} disabled={saving} data-testid="save-settings-btn">
            {saving ? <Loader2 className="animate-spin mr-2" size={16} /> : <Save className="mr-2" size={16} />}
            Save Changes
          </Button>
        </div>

        {/* Logo Section */}
        <div className="apple-card p-6">
          <h2 className="text-lg font-semibold text-stone-900 mb-4 flex items-center gap-2">
            <ImageIcon size={20} />
            Company Logo
          </h2>
          <p className="text-sm text-stone-500 mb-4">
            Upload your company logo. This will appear on your quotations and rate cards.
          </p>
          
          <div className="flex items-start gap-6">
            {/* Logo Preview */}
            <div className="w-32 h-32 rounded-lg border-2 border-dashed border-stone-300 flex items-center justify-center bg-stone-50 overflow-hidden">
              {profile.logo_url ? (
                <img 
                  src={/^(https?:|data:)/.test(profile.logo_url) ? profile.logo_url : `${API}${profile.logo_url}`}
                  alt="Company Logo"
                  className="w-full h-full object-contain"
                />
              ) : (
                <Building2 className="text-stone-300" size={48} />
              )}
            </div>
            
            {/* Upload Controls */}
            <div className="space-y-3">
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleLogoUpload}
                accept="image/png,image/jpeg,image/jpg,image/webp"
                className="hidden"
              />
              <Button
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingLogo}
                data-testid="upload-logo-btn"
              >
                {uploadingLogo ? (
                  <Loader2 className="animate-spin mr-2" size={16} />
                ) : (
                  <Upload className="mr-2" size={16} />
                )}
                Upload Logo
              </Button>
              
              {profile.logo_url && (
                <Button
                  variant="ghost"
                  className="text-red-600 hover:text-red-700"
                  onClick={handleRemoveLogo}
                  data-testid="remove-logo-btn"
                >
                  <Trash2 className="mr-2" size={16} />
                  Remove
                </Button>
              )}
              
              <p className="text-xs text-stone-400">
                PNG, JPG, WEBP (max 2MB)
              </p>
            </div>
          </div>
        </div>

        {/* Company Details */}
        <div className="apple-card p-6">
          <h2 className="text-lg font-semibold text-stone-900 mb-4 flex items-center gap-2">
            <Building2 size={20} />
            Company Details
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* iter-386u-k — Display Name (moved from Vendor Dashboard). Shown on Admin
                dashboards, quotes, and client-facing pages. */}
            <div className="md:col-span-2">
              <Label>Display Name</Label>
              <Input
                value={profile.display_name}
                onChange={(e) => setProfile({ ...profile, display_name: e.target.value })}
                placeholder="How you'd like to appear on quotes and client-facing pages"
                data-testid="display-name-input"
              />
              <p className="text-xs text-stone-400 mt-1">
                This name appears on Admin dashboards, quotations, and every client-facing page.
              </p>
            </div>
            
            <div>
              <Label>Company Name</Label>
              <Input
                value={profile.company_name}
                onChange={(e) => setProfile({ ...profile, company_name: e.target.value })}
                placeholder="Your Company Name"
                data-testid="company-name-input"
              />
            </div>
            
            <div>
              <Label>Contact Person</Label>
              <Input
                value={profile.contact_person}
                onChange={(e) => setProfile({ ...profile, contact_person: e.target.value })}
                placeholder="Primary Contact"
                data-testid="contact-person-input"
              />
            </div>
            
            <div>
              <Label className="flex items-center gap-1"><Phone size={14} /> Phone</Label>
              <Input
                value={profile.phone}
                onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
                placeholder="+91 XXXXX XXXXX"
                data-testid="phone-input"
              />
            </div>
            
            <div>
              <Label className="flex items-center gap-1"><Mail size={14} /> Email</Label>
              <Input
                value={profile.email}
                onChange={(e) => setProfile({ ...profile, email: e.target.value })}
                placeholder="company@example.com"
                data-testid="email-input"
              />
            </div>
            
            <div className="md:col-span-2">
              <Label className="flex items-center gap-1"><MapPin size={14} /> Address</Label>
              <Textarea
                value={profile.address}
                onChange={(e) => setProfile({ ...profile, address: e.target.value })}
                placeholder="Company Address"
                rows={2}
                data-testid="address-input"
              />
            </div>
          </div>
        </div>

        {/* Tax Information */}
        <div className="apple-card p-6">
          <h2 className="text-lg font-semibold text-stone-900 mb-4 flex items-center gap-2">
            <FileText size={20} />
            Tax Information
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>GSTIN</Label>
              <Input
                value={profile.gstin}
                onChange={(e) => setProfile({ ...profile, gstin: e.target.value.toUpperCase() })}
                placeholder="22AAAAA0000A1Z5"
                maxLength={15}
                data-testid="gstin-input"
              />
            </div>
            
            <div>
              <Label>PAN</Label>
              <Input
                value={profile.pan}
                onChange={(e) => setProfile({ ...profile, pan: e.target.value.toUpperCase() })}
                placeholder="AAAAA0000A"
                maxLength={10}
                data-testid="pan-input"
              />
            </div>
          </div>
        </div>

        {/* Description */}
        <div className="apple-card p-6">
          <h2 className="text-lg font-semibold text-stone-900 mb-4">About Your Company</h2>
          <Textarea
            value={profile.description}
            onChange={(e) => setProfile({ ...profile, description: e.target.value })}
            placeholder="Brief description of your company and services..."
            rows={4}
            data-testid="description-input"
          />
        </div>

        {/* Save Button (Bottom) */}
        <div className="flex justify-end">
          <Button onClick={handleSave} disabled={saving} size="lg" data-testid="save-settings-btn-bottom">
            {saving ? <Loader2 className="animate-spin mr-2" size={16} /> : <Check className="mr-2" size={16} />}
            Save All Changes
          </Button>
        </div>
      </div>
    </Layout>
  );
}
